import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Layout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { useTranslation } from 'react-i18next';
import {
    Layout as LayoutIcon,
    Plus,
    Save,
    Globe,
    GlobeLock,
    Pencil,
    Trash2,
    Loader2,
    Settings,
    Database,
    Grid3X3,
    Monitor,
    Tablet,
    Smartphone
} from 'lucide-react';
import { BoardTemplate, ChartItem, adminTemplateService, TEMPLATE_TYPES, TemplateType, SizeTier, SIZE_TIERS } from '../../lib/api/templates';
import { AddChartModal } from './AddChartModal';
import { PreviewDataSettings } from './PreviewDataSettings';
import { generateMockData, MockData } from '../../lib/mockData';
import { subDays } from 'date-fns';
import { ChartRenderer } from '../charts/ChartRenderer';
import {
    detectBreakpoint,
    calculateChartWidth,
    calculateChartHeight,
    resolveChartDimensions,
    compactHorizontal,
    generateResponsiveLayouts,
    GridItem
} from './chartStorage';
import { CHART_WIDTH_MAP } from '../../lib/api/templates';
import { AdminBoardGrid } from './components/AdminBoardGrid';

interface TemplateEditorProps {
    template: BoardTemplate | null;
    onTemplateUpdate: (template: BoardTemplate) => void;
}

interface DroppingItem {
    i: string;
    sizeTier: SizeTier;
    aspectRatio: string;
    w: number;
    h: number;
}

const ChartPlaceholder: React.FC<{ chartId: string; onRemove: () => void; mockData?: MockData; isDragging?: boolean }> = ({ chartId, onRemove, mockData, isDragging }) => {
    const { t } = useTranslation('common');
    const getChartName = (type: string) => {
        const translated = t(`charts.titles.${type}`, { defaultValue: '' });
        if (translated) return translated;
        return type
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    return (
        <div className="h-full flex flex-col bg-white border border-neutral-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow group relative">
            <div className="chart-drag-handle flex items-center justify-between px-3 py-2 border-b border-neutral-50 bg-neutral-50/50 cursor-move">
                <div className="flex items-center gap-2 overflow-hidden">
                    <div className="w-5 h-5 rounded bg-white border border-neutral-200 flex items-center justify-center shrink-0">
                        <LayoutIcon size={12} className="text-neutral-500" />
                    </div>
                    <span className="text-[11px] font-bold text-neutral-700 truncate capitalize">
                        {getChartName(chartId)}
                    </span>
                </div>
                <button
                    onClick={onRemove}
                    className={`p-1 text-neutral-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors relative z-10 ${isDragging ? 'opacity-0 pointer-events-none' : 'opacity-0 group-hover:opacity-100'}`}
                >
                    <Trash2 size={12} />
                </button>
            </div>
            <div className="flex-1 flex flex-col bg-white relative overflow-hidden">
                {mockData ? (
                    <ChartRenderer type={chartId} mockData={mockData} />
                ) : (
                    <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-neutral-50 to-neutral-100 relative">
                        <div className="text-center">
                            <div className="w-10 h-10 mx-auto bg-white rounded-lg border border-neutral-200 flex items-center justify-center mb-2">
                                <LayoutIcon size={20} className="text-neutral-400" />
                            </div>
                            <p className="text-[10px] text-neutral-400 font-medium">Preview placeholder</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export const TemplateEditor: React.FC<TemplateEditorProps> = ({ template, onTemplateUpdate }) => {
    const { t } = useTranslation('common');
    const [editedName, setEditedName] = useState(template?.name || '');
    const [isSaving, setIsSaving] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);
    const [description, setDescription] = useState(template?.description || '');
    const nameInputRef = useRef<HTMLInputElement>(null);

    const [mockData, setMockData] = useState<MockData | null>(null);
    const [mockSettings, setMockSettings] = useState({
        startDate: subDays(new Date(), 30),
        endDate: new Date(),
        currency: 'USD'
    });

    useEffect(() => {
        const data = generateMockData(mockSettings);
        setMockData(data);
    }, []);

    const handleRegenerateData = (settings: { startDate: Date; endDate: Date; currency: string }) => {
        setMockSettings(settings);
        const data = generateMockData(settings);
        setMockData(data);
    };

    useEffect(() => {
        if (template) {
            setEditedName(template.name);

            const migratedItems = (template.chartConfig || []).map(item => ({
                ...item,
                sizeTier: item.sizeTier || 'md',
                order: typeof item.order === 'number' ? item.order : (typeof item.y === 'number' ? item.y * 1000 + (item.x || 0) : 0),
                aspectRatio: item.aspectRatio || '16:9',
                _editBp: item._editBp || (item.x !== undefined ? 'xl' : undefined)
            }));

            const reordered = [...migratedItems].sort((a, b) => a.order - b.order)
                .map((item, index) => ({ ...item, order: index }));

            setChartItems(reordered);
            setSelectedType(template.templateType);
            setDescription(template.description || '');
            setHasUnsavedChanges(false);
        }
    }, [template]);

    const [isDragging, setIsDragging] = useState(false);
    const [showAddChart, setShowAddChart] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [showPreviewSettings, setShowPreviewSettings] = useState(false);
    const [showGrid, setShowGrid] = useState(true);
    const [droppingItem, setDroppingItem] = useState<DroppingItem | null>(null);
    const [chartItems, setChartItems] = useState<ChartItem[]>(template?.chartConfig || []);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [selectedType, setSelectedType] = useState<TemplateType>(template?.templateType || 'Other');
    const [previewDevice, setPreviewDevice] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');

    const gridItems: GridItem[] = React.useMemo(() => chartItems.map(c => ({
        i: c.instanceId,
        x: c.x || 0,
        y: c.y || 0,
        w: c.w || 4,
        h: c.h || 2,
        type: c.chartId,
        sizeTier: c.sizeTier,
        order: c.order,
        aspectRatio: c.aspectRatio,
        _editBp: c._editBp
    })), [chartItems]);

    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges]);

    const handleSave = async () => {
        if (!template) return;
        setIsSaving(true);
        try {
            const updated = await adminTemplateService.updateTemplate(template.id, {
                name: editedName,
                chartConfig: chartItems,
                templateType: selectedType,
                description: description
            });
            setHasUnsavedChanges(false);
            onTemplateUpdate(updated);
        } catch (error) {
            console.error('Error saving template:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handlePublishToggle = async () => {
        if (!template) return;

        if (template.status === 'draft' && chartItems.length === 0) {
            alert('Cannot publish an empty template. Add some charts first.');
            return;
        }

        setIsPublishing(true);
        try {
            let updated;
            if (template.status === 'draft') {
                updated = await adminTemplateService.publishTemplate(template.id);
            } else {
                updated = await adminTemplateService.unpublishTemplate(template.id);
            }
            onTemplateUpdate(updated);
        } catch (error) {
            console.error('Error toggling publish status:', error);
        } finally {
            setIsPublishing(false);
        }
    };

    const handleLayoutChangeComplete = useCallback((layout: Layout[], activeItemId?: string, isResize?: boolean) => {
        if (!template) return;

        const currentBp = detectBreakpoint(window.innerWidth);

        const currentGridItems: GridItem[] = chartItems.map(c => ({
            i: c.instanceId,
            x: c.x || 0,
            y: c.y || 0,
            w: c.w || 4,
            h: c.h || 2,
            type: c.chartId,
            sizeTier: c.sizeTier,
            order: c.order,
            aspectRatio: c.aspectRatio,
            _editBp: c._editBp
        }));

        const newItems = currentGridItems.map(item => {
            const layoutItem = layout.find(l => l.i === item.i);
            if (layoutItem) {
                if (item.i === activeItemId && isResize) {
                    let closestTier = item.sizeTier;
                    let minDistance = Infinity;

                    Object.entries(CHART_WIDTH_MAP).forEach(([tier, bpMap]) => {
                        const widthAtBp = bpMap[currentBp] ?? SIZE_TIERS[tier as SizeTier].preferred;
                        const distance = Math.abs(widthAtBp - layoutItem.w);
                        if (distance < minDistance) {
                            minDistance = distance;
                            closestTier = tier as SizeTier;
                        } else if (distance === minDistance) {
                            if (SIZE_TIERS[tier as SizeTier].preferred < SIZE_TIERS[closestTier].preferred) {
                                closestTier = tier as SizeTier;
                            }
                        }
                    });

                    return {
                        ...item,
                        x: layoutItem.x,
                        y: layoutItem.y,
                        w: layoutItem.w,
                        h: layoutItem.h,
                        sizeTier: closestTier,
                        aspectRatio: `${layoutItem.w}:${layoutItem.h}`,
                        _editBp: currentBp
                    };
                } else {
                    return {
                        ...item,
                        x: layoutItem.x,
                        y: layoutItem.y,
                        w: layoutItem.w,
                        h: layoutItem.h,
                        _editBp: currentBp
                    };
                }
            }
            return { ...item, _editBp: currentBp };
        });

        const compactedItems = compactHorizontal(newItems, activeItemId);

        const sorted = [...compactedItems].sort((a, b) => {
            const ay = Math.round(a.y);
            const by = Math.round(b.y);
            if (ay === by) return a.x - b.x;
            return ay - by;
        });

        const finalChartItems: ChartItem[] = sorted.map((gItem, index) => {
            const original = chartItems.find(c => c.instanceId === gItem.i);
            return {
                ...original!,
                order: index,
                x: gItem.x,
                y: gItem.y,
                w: gItem.w,
                h: gItem.h,
                sizeTier: gItem.sizeTier,
                aspectRatio: gItem.aspectRatio,
                _editBp: gItem._editBp
            };
        });

        setChartItems(finalChartItems);
        setHasUnsavedChanges(true);
    }, [template, chartItems]);


    const handleAddChart = (chartType: string, sizeTier: SizeTier, aspectRatio: string) => {
        const newInstanceId = `chart-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        const currentBp = detectBreakpoint(window.innerWidth);

        const localGridItems = chartItems.map(c => ({
            i: c.instanceId,
            x: c.x || 0,
            y: c.y || 0,
            w: c.w || 4,
            h: c.h || 2,
            type: c.chartId,
            sizeTier: c.sizeTier,
            order: c.order,
            aspectRatio: c.aspectRatio,
            _editBp: c._editBp
        }));

        const currentLayouts = generateResponsiveLayouts(localGridItems);
        const physicalItems = currentLayouts[currentBp] || localGridItems;

        const { w, h } = resolveChartDimensions({
            sizeTier,
            aspectRatio,
            w: calculateChartWidth(sizeTier, currentBp),
            h: calculateChartHeight(sizeTier, currentBp),
            i: 'temp',
            type: chartType,
            order: 0
        } as GridItem, currentBp);

        let maxY = 0;
        physicalItems.forEach((item: GridItem) => {
            const itemBottom = (item.y || 0) + (item.h || 2);
            if (itemBottom > maxY) maxY = itemBottom;
        });

        const newChart: ChartItem = {
            instanceId: newInstanceId,
            chartId: chartType,
            sizeTier,
            aspectRatio,
            order: chartItems.length,
            x: 0,
            y: maxY,
            w,
            h,
            _editBp: currentBp
        };

        const physicallyMapped = physicalItems.map((p: GridItem, i: number) => {
            const orig = chartItems.find(c => c.instanceId === p.i);
            return {
                ...orig,
                x: p.x,
                y: p.y,
                w: p.w,
                h: p.h,
                _editBp: currentBp,
                order: i
            } as ChartItem;
        });

        const combined = [...physicallyMapped, newChart];
        setChartItems(combined);
        setHasUnsavedChanges(true);
        setShowAddChart(false);
    };

    const handleRemoveChart = (instanceId: string) => {
        setChartItems(chartItems.filter(item => item.instanceId !== instanceId));
        setHasUnsavedChanges(true);
    };

    const handleDrop = (_layout: Layout[], item: Layout, _e: Event) => {
        const data = droppingItem;
        if (!data) return;

        const newInstanceId = `chart-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        const updatedItems = chartItems.map(chart => {
            const match = _layout.find(l => l.i === chart.instanceId);
            if (match) {
                return { ...chart, x: match.x, y: match.y };
            }
            return chart;
        });

        const newChart: ChartItem = {
            instanceId: newInstanceId,
            chartId: data.i,
            sizeTier: data.sizeTier,
            aspectRatio: data.aspectRatio,
            order: chartItems.length,
            x: item.x,
            y: item.y,
            w: data.w || 4,
            h: data.h || 4
        };

        const combined: ChartItem[] = [...updatedItems, newChart];

        const localGridItems: GridItem[] = combined.map(c => ({
            i: c.instanceId,
            type: c.chartId,
            x: c.x || 0,
            y: c.y || 0,
            w: c.w || 4,
            h: c.h || 2,
            sizeTier: c.sizeTier,
            order: c.order,
            aspectRatio: c.aspectRatio,
            _editBp: c._editBp
        }));

        const compacted = compactHorizontal(localGridItems, newInstanceId);

        const sorted = compacted.sort((a, b) => {
            const ay = Math.round(a.y);
            const by = Math.round(b.y);
            if (ay === by) return a.x - b.x;
            return ay - by;
        });

        const reordered: ChartItem[] = sorted.map((gItem, index) => {
            const original = combined.find(c => c.instanceId === gItem.i);
            return {
                ...original!,
                order: index,
                x: gItem.x,
                y: gItem.y,
                w: gItem.w,
                h: gItem.h,
                sizeTier: gItem.sizeTier,
                aspectRatio: gItem.aspectRatio,
                _editBp: gItem._editBp
            };
        });

        setChartItems(reordered);
        setDroppingItem(null);
        setHasUnsavedChanges(true);
        setShowAddChart(false);
    };

    const handleDragStart = (chartType: string, sizeTier: SizeTier, aspectRatio: string) => {
        const initialW = calculateChartWidth(sizeTier, 'lg');
        const [ratioW, ratioH] = aspectRatio.split(':').map(Number);
        const initialH = Math.max(1, Math.round(initialW * (ratioH / ratioW)));

        setDroppingItem({ i: chartType, sizeTier, aspectRatio, w: initialW, h: initialH });
        setTimeout(() => {
            setShowAddChart(false);
        }, 50);
    };

    if (!template) {
        return (
            <div className="h-full flex flex-col items-center justify-center text-neutral-400 bg-neutral-50/30">
                <LayoutIcon size={48} strokeWidth={1} className="mb-4 opacity-20" />
                <p className="text-sm font-medium">Select a template to start editing</p>
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col bg-white">
            {/* Header */}
            <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-4 flex-1">
                    <div className="relative group">
                        <input
                            ref={nameInputRef}
                            type="text"
                            value={editedName}
                            onChange={(e) => {
                                setEditedName(e.target.value);
                                setHasUnsavedChanges(true);
                            }}
                            className="text-lg font-bold text-neutral-900 bg-transparent border-b border-transparent hover:border-neutral-200 focus:border-blue-500 focus:outline-none px-1 py-0.5 transition-all"
                            placeholder="Template Name"
                        />
                        <Pencil size={14} className="absolute -right-5 top-1/2 -translate-y-1/2 text-neutral-300 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${template.status === 'published'
                            ? 'bg-emerald-50 text-emerald-600 border border-emerald-100'
                            : 'bg-amber-50 text-amber-600 border border-amber-100'
                            }`}>
                            {template.status}
                        </span>
                        {hasUnsavedChanges && (
                            <span className="text-[10px] font-medium text-amber-500 flex items-center gap-1">
                                <span className="w-1 h-1 rounded-full bg-amber-500 animate-pulse" />
                                {t('templates.unsavedChanges')}
                            </span>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <div className="flex items-center bg-neutral-100 rounded-lg p-0.5 border border-neutral-200 mr-2">
                        <button
                            onClick={() => setPreviewDevice('desktop')}
                            className={`p-1.5 rounded-md transition-all ${previewDevice === 'desktop' ? 'bg-white shadow text-blue-600' : 'text-neutral-500 hover:text-neutral-700'}`}
                            title={t('templates.viewOnDesktop')}
                        >
                            <Monitor size={14} />
                        </button>
                        <button
                            onClick={() => setPreviewDevice('tablet')}
                            className={`p-1.5 rounded-md transition-all ${previewDevice === 'tablet' ? 'bg-white shadow text-blue-600' : 'text-neutral-500 hover:text-neutral-700'}`}
                            title={t('templates.viewOnTablet')}
                        >
                            <Tablet size={14} />
                        </button>
                        <button
                            onClick={() => setPreviewDevice('mobile')}
                            className={`p-1.5 rounded-md transition-all ${previewDevice === 'mobile' ? 'bg-white shadow text-blue-600' : 'text-neutral-500 hover:text-neutral-700'}`}
                            title={t('templates.viewOnMobile')}
                        >
                            <Smartphone size={14} />
                        </button>
                    </div>

                    <button
                        onClick={() => setShowSettings(!showSettings)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${showSettings
                            ? 'bg-neutral-900 text-white border-neutral-900'
                            : 'border-neutral-200 text-neutral-600 hover:bg-neutral-50'
                            }`}
                        title={t('templates.templateSettings')}
                    >
                        <Settings size={14} />
                        {t('common.settings')}
                    </button>

                    <button
                        onClick={() => setShowPreviewSettings(true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-neutral-200 text-neutral-600 hover:bg-neutral-50 transition-colors"
                        title="Configure preview data"
                    >
                        <Database size={14} />
                    </button>

                    <button
                        onClick={() => setShowGrid(!showGrid)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${showGrid
                            ? 'bg-neutral-900 text-white border-neutral-900'
                            : 'border-neutral-200 text-neutral-600 hover:bg-neutral-50'
                            }`}
                        title={showGrid ? 'Hide grid' : 'Show grid'}
                    >
                        <Grid3X3 size={14} />
                    </button>

                    <button
                        onClick={() => setShowAddChart(true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all shadow-sm shadow-blue-200"
                    >
                        <Plus size={14} />
                        Add Chart
                    </button>

                    <div className="w-px h-6 bg-neutral-200 mx-1" />

                    <button
                        onClick={handleSave}
                        disabled={isSaving || !hasUnsavedChanges}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-neutral-200 text-neutral-700 rounded-lg hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                        {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                        {t('actions.save')}
                    </button>

                    <button
                        onClick={handlePublishToggle}
                        disabled={isPublishing}
                        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${template.status === 'published'
                            ? 'border border-red-200 text-red-600 hover:bg-red-50'
                            : 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm shadow-emerald-200'
                            }`}
                    >
                        {isPublishing ? (
                            <Loader2 size={14} className="animate-spin" />
                        ) : template.status === 'published' ? (
                            <>
                                <GlobeLock size={14} />
                                {t('actions.unpublish')}
                            </>
                        ) : (
                            <>
                                <Globe size={14} />
                                {t('actions.publish')}
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Settings Sub-header */}
            {showSettings && (
                <div className="px-6 py-4 bg-neutral-50 border-b border-neutral-200 animate-in slide-in-from-top duration-200">
                    <div className="max-w-4xl mx-auto flex gap-8">
                        <div className="w-64">
                            <label className="block text-[10px] font-bold text-neutral-400 uppercase tracking-wider mb-1.5">Template Category</label>
                            <select
                                value={selectedType}
                                onChange={(e) => {
                                    setSelectedType(e.target.value as TemplateType);
                                    setHasUnsavedChanges(true);
                                }}
                                className="w-full text-xs border border-neutral-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                            >
                                {TEMPLATE_TYPES.map(type => (
                                    <option key={type} value={type}>{type}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex-1">
                            <label className="block text-[10px] font-bold text-neutral-400 uppercase tracking-wider mb-1.5">{t('common.description')}</label>
                            <input
                                type="text"
                                value={description}
                                onChange={(e) => {
                                    setDescription(e.target.value);
                                    setHasUnsavedChanges(true);
                                }}
                                placeholder="Optional description..."
                                className="w-full text-xs border border-neutral-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Main Area */}
            <div className="flex-1 overflow-hidden p-4 bg-neutral-50/50 flex flex-col items-center justify-center">
                {chartItems.length === 0 ? (
                    <div
                        onClick={() => setShowAddChart(true)}
                        className="w-full h-full flex flex-col items-center justify-center border-2 border-dashed border-neutral-200 rounded-2xl bg-white cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-all group shadow-sm"
                    >
                        <div className="w-16 h-16 bg-neutral-100 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                            <Plus size={24} className="text-neutral-400 group-hover:text-blue-500 transition-colors" />
                        </div>
                        <h4 className="text-sm font-bold text-neutral-700 mb-1">{t('templates.empty_canvas_title')}</h4>
                        <p className="text-xs text-neutral-500">{t('templates.empty_canvas_desc')}</p>
                    </div>
                ) : (
                    <div
                        className={`relative bg-white rounded-2xl border border-neutral-200 shadow-sm transition-all duration-300 flex flex-col ${previewDevice === 'mobile' ? 'w-[375px]' :
                            previewDevice === 'tablet' ? 'w-[768px]' :
                                'w-full'
                            } h-full overflow-hidden`}
                    >
                        <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 custom-scrollbar">
                            <AdminBoardGrid
                                items={gridItems}
                                renderItem={(item) => {
                                    const original = chartItems.find(c => c.instanceId === item.i);
                                    if (!original) return null;
                                    return (
                                        <div className="h-full rounded-xl overflow-hidden hover:ring-2 hover:ring-blue-400/50">
                                            <ChartPlaceholder
                                                chartId={original.chartId}
                                                onRemove={() => handleRemoveChart(original.instanceId)}
                                                mockData={mockData || undefined}
                                                isDragging={isDragging}
                                            />
                                        </div>
                                    );
                                }}
                                onLayoutChange={() => { }}
                                onDragStop={(l, _, newItem) => {
                                    setIsDragging(false);
                                    handleLayoutChangeComplete(l, newItem.i, false);
                                }}
                                onResizeStop={(l, _, newItem) => {
                                    setIsDragging(false);
                                    handleLayoutChangeComplete(l, newItem.i, true);
                                }}
                                onDrop={handleDrop}
                                onDragStart={() => setIsDragging(true)}
                                onResizeStart={() => setIsDragging(true)}
                                droppingItem={droppingItem ? { i: droppingItem.i, w: droppingItem.w, h: droppingItem.h } : undefined}
                                showGrid={showGrid}
                                onDrag={(_layout, _oldItem, newItem) => {
                                    if (newItem.i === '__dropping-elem__') {
                                        newItem.x = Math.max(0, newItem.x - Math.floor(newItem.w / 2));
                                        newItem.y = Math.max(0, newItem.y - Math.floor(newItem.h / 2));
                                    }
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Modals */}
            <AddChartModal
                isOpen={showAddChart}
                onClose={() => setShowAddChart(false)}
                onAddChart={handleAddChart}
                onDragStart={handleDragStart}
                onDragEnd={() => setDroppingItem(null)}
            />

            <PreviewDataSettings
                isOpen={showPreviewSettings}
                onClose={() => setShowPreviewSettings(false)}
                onRegenerate={handleRegenerateData}
                currentSettings={mockSettings}
            />
        </div>
    );
};
